import { z } from "zod";

import { appointmentSlotSchema, availabilityAppointmentTypeSchema } from "@/shared/agent-api/requests";
import { availabilityAppointmentSlotResponseSchema } from "@/shared/agent-api/responses";
import { usPostalCodeSchema } from "@/shared/models/address";
import { AppointmentType } from "@/shared/models/appointment";
import { PersonGender } from "@/shared/models/person";
import { timePreferenceSchema } from "@/shared/models/scheduling-availability";
import { USState } from "@/src/lib/us-states";
import { normalizePhoneNumber } from "@/src/models/common-schema";

/**
 * Zod schemas for the online scheduling (unauthenticated, patient-facing) API.
 * These are intentionally decoupled from the agent (`/api/agent/*`) schemas: the
 * public endpoints resolve the tenant/group server-side from the request host (never
 * from the client) and never require a `patientId`.
 */

const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");

/** Max days between fromDate and toDate for a single availability request. */
export const ONLINE_SCHEDULING_MAX_AVAILABILITY_WINDOW_DAYS = 120;
export const ONLINE_SCHEDULING_PHONE_ERROR_MESSAGE = "Enter a valid US phone number (for example, 615-555-0100)";

export function isValidOnlineSchedulingPhone(value: string): boolean {
  return normalizePhoneNumber(value) !== null;
}

function daysBetweenDateOnly(start: string, end: string): number {
  const [sy, sm, sd] = start.split("-").map(Number);
  const [ey, em, ed] = end.split("-").map(Number);
  const startMs = Date.UTC(sy, sm - 1, sd);
  const endMs = Date.UTC(ey, em - 1, ed);

  return Math.round((endMs - startMs) / 86_400_000);
}

/**
 * Strip chars that break our pipe-delimited booking notes or inject structure into downstream
 * EHR / agent surfaces.
 */
export function sanitizePatientText(value: string, maxLen: number): string {
  return value
    .trim()
    .replace(/[\r\n\t|]+/g, " ")
    .replace(/\s+/g, " ")
    .slice(0, maxLen)
    .trim();
}

const sanitizedName = z
  .string()
  .trim()
  .min(1)
  .transform((v) => sanitizePatientText(v, 80))
  .pipe(z.string().min(1, "Name is required"));

const optionalSanitizedText = (maxLen: number) =>
  z
    .string()
    .trim()
    .max(maxLen)
    .optional()
    .transform((v) => (v?.trim() ? sanitizePatientText(v, maxLen) : undefined));

/** Shared client/server requirement for portals that require insurance. */
export const requiredOnlineSchedulingInsuranceSchema = z.object({
  payer: z
    .string()
    .trim()
    .max(120)
    .refine((value) => sanitizePatientText(value, 120).length > 0, "Enter your insurance carrier"),
  memberId: z
    .string()
    .trim()
    .max(60)
    .refine((value) => sanitizePatientText(value, 60).length > 0, "Enter your insurance member ID"),
});

export const onlineSchedulingHomeAddressSchema = z
  .object({
    line1: z
      .string()
      .trim()
      .min(1, "Enter your street address")
      .max(120)
      .transform((value) => sanitizePatientText(value, 120))
      .pipe(z.string().min(1, "Enter your street address")),
    line2: z
      .string()
      .trim()
      .max(120)
      .nullish()
      .transform((value) => (value ? sanitizePatientText(value, 120) : null)),
    city: z
      .string()
      .trim()
      .min(1, "Enter your city")
      .max(80)
      .transform((value) => sanitizePatientText(value, 80))
      .pipe(z.string().min(1, "Enter your city")),
    state: z.nativeEnum(USState),
    postalCode: z.string().trim().pipe(usPostalCodeSchema),
    placeId: z.string().trim().min(1).nullish(),
  })
  .transform((address) => ({ ...address, country: "USA", placeId: address.placeId ?? null }));

export const onlineSchedulingRegionQuerySchema = z.object({
  regionCode: z.string().trim().min(1).max(80).nullish(),
});

/** Minimal, patient-friendly intake collected by the public form. */
const onlineSchedulingIntakeBaseSchema = z.object({
  firstName: sanitizedName,
  lastName: sanitizedName,
  // Accepts MM/DD/YYYY or YYYY-MM-DD; normalized server-side by the intake service.
  dateOfBirth: z.string().trim().min(1, "Date of birth is required"),
  phone: z.string().trim().refine(isValidOnlineSchedulingPhone, ONLINE_SCHEDULING_PHONE_ERROR_MESSAGE),
  // Patient's gender, collected by the form's dropdown and persisted to the patient in Valerie +
  // the EHR. Optional at the boundary; the client marks it required.
  gender: z.nativeEnum(PersonGender).optional(),
  email: z.string().trim().email("Enter a valid email").optional().or(z.literal("")),
  homeAddress: onlineSchedulingHomeAddressSchema,
  reasonForVisit: optionalSanitizedText(200),
  selfPay: z.boolean().optional(),
  insurance: z
    .object({
      payer: z.string().trim().max(120).optional(),
      memberId: z.string().trim().max(60).optional(),
    })
    .optional()
    .transform((insurance) => {
      if (!insurance) {
        return undefined;
      }

      const payer = insurance.payer?.trim() ? sanitizePatientText(insurance.payer, 120) : undefined;
      const memberId = insurance.memberId?.trim() ? sanitizePatientText(insurance.memberId, 60) : undefined;

      if (!payer && !memberId) {
        return undefined;
      }

      return { payer, memberId };
    }),
  // Parent/legal guardian, collected by the form for patients under 18. Kept optional at the schema
  // boundary (age is derived from DOB, which several formats parse) — the client enforces requiredness.
  guardian: z
    .object({
      firstName: z.string().trim().max(80).optional(),
      lastName: z.string().trim().max(80).optional(),
      phone: z
        .string()
        .trim()
        .max(40)
        .optional()
        .refine(
          (value) => !value || isValidOnlineSchedulingPhone(value),
          "Enter a valid 10-digit guardian phone number"
        ),
    })
    .optional()
    .transform((guardian) => {
      if (!guardian) {
        return undefined;
      }

      const firstName = guardian.firstName?.trim() ? sanitizePatientText(guardian.firstName, 80) : undefined;
      const lastName = guardian.lastName?.trim() ? sanitizePatientText(guardian.lastName, 80) : undefined;
      const phone = guardian.phone?.trim() ? sanitizePatientText(guardian.phone, 40) : undefined;

      if (!firstName && !lastName && !phone) {
        return undefined;
      }

      return { firstName, lastName, phone };
    }),
});

function refineOnlineSchedulingIntake<T extends z.ZodTypeAny>(schema: T) {
  return schema.superRefine((data, ctx) => {
    const insurance = (data as OnlineSchedulingIntakeShape).insurance;

    if ((data as OnlineSchedulingIntakeShape).selfPay && insurance) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Choose self-pay or insurance, not both",
        path: ["insurance"],
      });
    }

    const memberId = insurance?.memberId;
    const payer = insurance?.payer;

    if (memberId && !payer) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Insurance carrier is required when a member ID is provided",
        path: ["insurance", "payer"],
      });
    }
  });
}

type OnlineSchedulingIntakeShape = z.infer<typeof onlineSchedulingIntakeBaseSchema>;

export const onlineSchedulingIntakeSchema = refineOnlineSchedulingIntake(onlineSchedulingIntakeBaseSchema);

export type OnlineSchedulingIntake = z.infer<typeof onlineSchedulingIntakeSchema>;

export const onlineSchedulingAvailabilityRequestSchema = z
  .object({
    patientDetails: onlineSchedulingIntakeSchema.optional(),
    regionCode: onlineSchedulingRegionQuerySchema.shape.regionCode,
    /**
     * Valerie location UUID. Omit it only for a provider-first search, which must identify exactly
     * one practitioner and searches that provider across every online-scheduling location.
     */
    location: z.string().min(1).nullish(),
    appointmentType: availabilityAppointmentTypeSchema.default(AppointmentType.NEW_PATIENT_CHECKIN),
    fromDate: dateOnly,
    toDate: dateOnly,
    timePreference: z.array(timePreferenceSchema).optional(),
    /** Optional filter to a subset of providers (Valerie practitioner UUIDs). */
    practitionerIds: z.array(z.string().min(1)).nullish(),
  })
  // Order and window-size are timezone-independent; the "not in the past" check is done server-side
  // against the clinic's timezone (see getOnlineSchedulingAvailability).
  .refine((data) => data.fromDate <= data.toDate, { message: "fromDate must be on or before toDate", path: ["toDate"] })
  .refine((data) => daysBetweenDateOnly(data.fromDate, data.toDate) <= ONLINE_SCHEDULING_MAX_AVAILABILITY_WINDOW_DAYS, {
    message: `Date window cannot exceed ${ONLINE_SCHEDULING_MAX_AVAILABILITY_WINDOW_DAYS} days`,
    path: ["toDate"],
  })
  .superRefine((data, ctx) => {
    if (!data.location && data.practitionerIds?.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Provider-first availability requires exactly one practitioner",
        path: ["practitionerIds"],
      });
    }
  });

export type OnlineSchedulingAvailabilityRequest = z.infer<typeof onlineSchedulingAvailabilityRequestSchema>;

export const onlineSchedulingPatientOptionsRequestSchema = refineOnlineSchedulingIntake(
  onlineSchedulingIntakeBaseSchema.extend({
    initialProviderId: z.string().min(1).nullish(),
    initialLocationId: z.string().min(1).nullish(),
    regionCode: onlineSchedulingRegionQuerySchema.shape.regionCode,
  })
);
export type OnlineSchedulingPatientOptionsRequest = z.infer<typeof onlineSchedulingPatientOptionsRequestSchema>;

export const onlineSchedulingEligibilityRequestSchema = refineOnlineSchedulingIntake(
  onlineSchedulingIntakeBaseSchema.extend({
    regionCode: onlineSchedulingRegionQuerySchema.shape.regionCode,
    reasonForVisit: z
      .string()
      .trim()
      .min(1)
      .max(200)
      .transform((value) => sanitizePatientText(value, 200))
      .pipe(z.string().min(1)),
  })
).superRefine((data, ctx) => {
  if (data.selfPay) return;
  const insurance = requiredOnlineSchedulingInsuranceSchema.safeParse(data.insurance);

  if (!insurance.success) {
    for (const issue of insurance.error.issues) {
      ctx.addIssue({ ...issue, path: ["insurance", ...issue.path] });
    }
  }
});

export type OnlineSchedulingEligibilityRequest = z.infer<typeof onlineSchedulingEligibilityRequestSchema>;

export const onlineSchedulingBookRequestSchema = refineOnlineSchedulingIntake(
  onlineSchedulingIntakeBaseSchema.extend({
    regionCode: onlineSchedulingRegionQuerySchema.shape.regionCode,
    location: z.string().min(1),
    appointmentSlot: appointmentSlotSchema,
    // Whether the patient explicitly consented to be contacted about the appointment (the review
    // step's consent checkbox). Recorded as an SMS-consent row when booking succeeds.
    smsConsent: z.boolean().default(false),
  })
);

export type OnlineSchedulingBookRequest = z.infer<typeof onlineSchedulingBookRequestSchema>;

export enum OnlineSchedulingPatientMatch {
  Created = "created",
  Matched = "matched",
}

export const onlineSchedulingBookResponseSchema = z.object({
  patientId: z.string().min(1),
  patientMatch: z.enum(OnlineSchedulingPatientMatch),
  confirmedAppointment: z.object({
    valerieAppointmentId: z.string().optional(),
    appointmentId: z.string(),
    dateTime: z.string(),
    locationId: z.string(),
    locationName: z.string(),
    providerDisplayName: z.string().nullable(),
    status: z.string().optional(),
  }),
});

export type OnlineSchedulingBookResponse = z.infer<typeof onlineSchedulingBookResponseSchema>;

export const onlineSchedulingAvailabilityResponseSchema = z.object({
  slots: z.array(availabilityAppointmentSlotResponseSchema.extend({ optionRef: z.string().min(1).optional() })),
  searchedDateRanges: z.array(z.string()),
});

export type OnlineSchedulingAvailabilityResponse = z.infer<typeof onlineSchedulingAvailabilityResponseSchema>;

export const onlineSchedulingLocationSchema = z.object({
  locationId: z.string().min(1),
  displayName: z.string().min(1),
  ehrId: z.string().min(1),
  city: z.string().nullable(),
  state: z.string().nullable(),
  addressLine1: z.string().nullable(),
});

export type OnlineSchedulingLocation = z.infer<typeof onlineSchedulingLocationSchema>;

export const onlineSchedulingProviderSchema = z.object({
  practitionerId: z.string().min(1),
  ehrId: z.string().min(1),
  displayName: z.string().min(1),
});

export type OnlineSchedulingProvider = z.infer<typeof onlineSchedulingProviderSchema>;

export const onlineSchedulingRegionSchema = z.object({
  regionCode: z.string().min(1),
  displayName: z.string().min(1),
  stateNames: z.array(z.string().min(1)).optional(),
  clinicPhoneNumber: z.string().min(1).nullish(),
  timezone: z.string().min(1),
  logoUrl: z.string().min(1).nullish(),
  logoOnDarkBackground: z.boolean().optional(),
});

export type OnlineSchedulingRegion = z.infer<typeof onlineSchedulingRegionSchema>;

export const onlineSchedulingLocationsResponseSchema = z.object({
  locations: z.array(onlineSchedulingLocationSchema),
});

export type OnlineSchedulingLocationsResponse = z.infer<typeof onlineSchedulingLocationsResponseSchema>;

export const onlineSchedulingOptionsResponseSchema = z.object({
  locations: z.array(onlineSchedulingLocationSchema),
  providers: z.array(onlineSchedulingProviderSchema),
});

export type OnlineSchedulingOptionsResponse = z.infer<typeof onlineSchedulingOptionsResponseSchema>;

export const onlineSchedulingReferralWindowSchema = z.object({
  isUrgent: z.boolean(),
  fromDate: dateOnly,
  toDate: dateOnly,
  maxWindowDays: z.number().int().nonnegative(),
});
export type OnlineSchedulingReferralWindow = z.infer<typeof onlineSchedulingReferralWindowSchema>;

export const onlineSchedulingEligibilityResponseSchema = onlineSchedulingOptionsResponseSchema.extend({
  isReturningPatient: z.boolean(),
  referralWindow: onlineSchedulingReferralWindowSchema.nullish(),
});
export type OnlineSchedulingEligibilityResponse = z.infer<typeof onlineSchedulingEligibilityResponseSchema>;

export const onlineReschedulingPatientDetailsSchema = onlineSchedulingIntakeBaseSchema.pick({
  firstName: true,
  lastName: true,
  dateOfBirth: true,
  phone: true,
  guardian: true,
});
export type OnlineReschedulingPatientDetails = z.infer<typeof onlineReschedulingPatientDetailsSchema>;

export const onlineSchedulingAppointmentsRequestSchema = z.object({
  patientDetails: onlineReschedulingPatientDetailsSchema,
});
export type OnlineSchedulingAppointmentsRequest = z.infer<typeof onlineSchedulingAppointmentsRequestSchema>;

export const onlineSchedulingExistingAppointmentSchema = z.object({
  id: z.string().min(1),
  appointmentActionId: z.string().min(1),
  start: z.string().min(1),
  appointmentType: z.enum(AppointmentType),
  appointmentTypeName: z.string().min(1),
  providerDisplayName: z.string().nullable(),
  locationName: z.string().nullable(),
});
export type OnlineSchedulingExistingAppointment = z.infer<typeof onlineSchedulingExistingAppointmentSchema>;

export const onlineSchedulingAppointmentsResponseSchema = onlineSchedulingOptionsResponseSchema.extend({
  appointments: z.array(onlineSchedulingExistingAppointmentSchema),
});
export type OnlineSchedulingAppointmentsResponse = z.infer<typeof onlineSchedulingAppointmentsResponseSchema>;

export const onlineReschedulingAvailabilityRequestSchema = onlineSchedulingAppointmentsRequestSchema
  .extend({
    appointmentActionId: z.string().min(1),
    fromDate: dateOnly,
    toDate: dateOnly,
  })
  .refine(
    (data) =>
      data.fromDate <= data.toDate &&
      daysBetweenDateOnly(data.fromDate, data.toDate) <= ONLINE_SCHEDULING_MAX_AVAILABILITY_WINDOW_DAYS,
    {
      message: `Choose a date range of at most ${ONLINE_SCHEDULING_MAX_AVAILABILITY_WINDOW_DAYS} days`,
      path: ["toDate"],
    }
  );
export type OnlineReschedulingAvailabilityRequest = z.infer<typeof onlineReschedulingAvailabilityRequestSchema>;

export const onlineReschedulingRequestSchema = onlineSchedulingAppointmentsRequestSchema.extend({
  optionRef: z.string().min(1).max(65_536),
});
export type OnlineReschedulingRequest = z.infer<typeof onlineReschedulingRequestSchema>;

export enum OnlineReschedulingOutcome {
  Confirmed = "confirmed",
  SlotUnavailable = "slot_unavailable",
  StaffRequired = "staff_required",
  StaleSelection = "stale_selection",
}

export const onlineReschedulingResponseSchema = z.discriminatedUnion("outcome", [
  z.object({ outcome: z.literal(OnlineReschedulingOutcome.Confirmed), booking: onlineSchedulingBookResponseSchema }),
  z.object({ outcome: z.literal(OnlineReschedulingOutcome.SlotUnavailable) }),
  z.object({ outcome: z.literal(OnlineReschedulingOutcome.StaffRequired) }),
  z.object({ outcome: z.literal(OnlineReschedulingOutcome.StaleSelection) }),
]);
export type OnlineReschedulingResponse = z.infer<typeof onlineReschedulingResponseSchema>;
